'use client';

import { useEffect, useMemo, useRef } from 'react';
import { useRouter } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { Plus } from 'lucide-react';
import { AuthTokenService } from '@/shared/lib/auth-token-service';
import { trackEvent, AnalyticsEvent } from '@/shared/lib/analytics';
import { useProjects } from '@/features/project/hooks/use-projects';
import { useLastProject } from '@/shared/hooks/use-last-project';
import { NewProjectDialog, useNewProjectStore } from '@/features/project';
import { Button } from '@/shared/ui/shadcn/button';
import { LegalFooterLinks } from '@/shared/ui/LegalFooterLinks';

interface PageProps {
  params: Promise<Record<string, never>>;
  searchParams: Promise<Record<string, never>>;
}

/**
 * Root client resolver — the building page is the app home now (the Dashboard
 * was removed). For an authenticated user we resolve the active building
 * (last-opened → first) and redirect to `/project/{id}`. With no buildings we
 * show a first-run "create your first building" state.
 *
 * Unauthenticated visitors get a public welcome screen (NOT a redirect to
 * login): Paddle's domain-approval bot fetches `/` without executing JS, so
 * the server-rendered HTML must show real product content with visible
 * terms/privacy/pricing links. A JS redirect to a login wall is the top
 * documented reason Paddle rejects a domain. Auth-token presence is read
 * synchronously so there is no flash of the wrong destination.
 */
export default function Home(_props: PageProps) {
  const t = useTranslations('common');
  const tHome = useTranslations('publicHome');
  const tAuth = useTranslations('auth');
  const tGrid = useTranslations('projectGrid');
  const router = useRouter();
  const openNewProject = useNewProjectStore((s) => s.open);

  const hasToken =
    typeof window !== 'undefined' ? !!AuthTokenService.getTokenSync() : false;

  const { data, isLoading } = useProjects({ enabled: hasToken });
  const projects = data?.data;
  const { lastProjectId } = useLastProject();

  const targetProjectId = useMemo(() => {
    if (!projects || projects.length === 0) return null;
    if (lastProjectId && projects.some((p) => p.id === lastProjectId)) {
      return lastProjectId;
    }
    return projects[0]?.id ?? null;
  }, [projects, lastProjectId]);

  useEffect(() => {
    if (hasToken && targetProjectId) {
      router.replace(`/project/${targetProjectId}`);
    }
  }, [hasToken, targetProjectId, router]);

  // Early-funnel: the empty buildings home (no projects yet) was shown. Fire
  // once per mount — the ref guards against re-fire on subsequent renders.
  const emptyStateTrackedRef = useRef(false);
  const showsEmptyState =
    hasToken && !isLoading && !!projects && projects.length === 0;
  useEffect(() => {
    if (showsEmptyState && !emptyStateTrackedRef.current) {
      emptyStateTrackedRef.current = true;
      trackEvent(AnalyticsEvent.HOME_EMPTY_STATE_VIEWED);
    }
  }, [showsEmptyState]);

  // Authenticated but no buildings yet → first-run create state.
  if (hasToken && !isLoading && projects && projects.length === 0) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 px-6 text-center">
        <Button onClick={openNewProject}>
          <Plus className="h-4 w-4" />
          {tGrid('createFirst')}
        </Button>
        <NewProjectDialog />
      </div>
    );
  }

  // Authenticated and resolving the target building — brief redirect screen.
  if (hasToken) {
    return (
      <div className="flex min-h-screen flex-col">
        <div className="flex flex-1 items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-foreground mx-auto"></div>
            <p className="mt-4 text-muted-foreground">{t('redirecting')}</p>
          </div>
        </div>
        <LegalFooterLinks
          className="text-muted-foreground"
          linkClassName="hover:text-foreground"
        />
      </div>
    );
  }

  // Public welcome screen — the server-rendered output of `/` (SSR runs with
  // no auth token). Paddle's domain-approval bot does not execute JS, so this
  // markup must contain real content plus terms/privacy/pricing links as
  // plain anchors. Never replace this branch with a redirect to login.
  return (
    <div className="flex min-h-screen flex-col">
      <main className="flex flex-1 flex-col items-center justify-center gap-6 px-6 text-center">
        <div className="max-w-md space-y-3">
          <h1 className="text-screen-title tracking-tight">
            {tHome('title')}
          </h1>
          <p className="text-muted-foreground">{tHome('subtitle')}</p>
        </div>
        <div className="flex flex-wrap items-center justify-center gap-3">
          <Button asChild>
            <a href="/auth/login">{tAuth('login.submit')}</a>
          </Button>
          <Button asChild variant="outline">
            <a href="/auth/register">{tAuth('register.title')}</a>
          </Button>
        </div>
      </main>
      <LegalFooterLinks
        className="text-muted-foreground"
        linkClassName="hover:text-foreground"
      />
    </div>
  );
}
